import type { IrsStatus, TaxYear } from '@divorceos/schema';
import { byJoint, yearData, type FederalTables } from './data.js';
import { cents, clamp0, scheduleTax } from './schedule.js';

export interface W4Input {
  /** Step 3: annual credit amount for dependents (children x 2,000 + others x 500). */
  dependentsCredit: number;
  /** Step 4(b): deductions beyond the standard deduction. */
  deductions: number;
  /** Step 4(a): other income (not from jobs). */
  otherIncome: number;
  /** Step 4(c): extra withholding per year (optional). */
  extraWithholding?: number;
}

const n = (x: number | undefined): number => (typeof x === 'number' && Number.isFinite(x) ? x : 0);

/**
 * Pub 15-T Worksheet 1A, annual "STANDARD withholding rate schedule" (2020+ Form W-4, Step 2 unchecked):
 * adjusted wages = wages + 4(a) - 4(b) - 12,900 (MFJ) / 8,600 (others); tentative withholding from the
 * schedule; minus Step 3 credits (not below zero); plus 4(c).
 */
export function federalWithholding(year: TaxYear, status: IrsStatus, annualWages: number, w4: W4Input, tables: FederalTables = 'irs'): number {
  const wh = yearData(year, tables).withholding;
  // us_tax.dll's US_WH_Tax (notes/us_tax.md §8) looks the wages up in the STANDARD schedule directly — it never
  // subtracts the Worksheet 1A line 1h allowance (12,900 / 8,600); the compat tables reproduce that.
  const allowance = tables === 'xspouse' ? 0 : byJoint(wh.w4StandardAllowance, status);
  const adjusted = clamp0(n(annualWages) + n(w4.otherIncome) - n(w4.deductions) - allowance);
  const { tax } = scheduleTax(wh.bracketTops[status], wh.rates, adjusted);
  return cents(clamp0(tax - n(w4.dependentsCredit)) + n(w4.extraWithholding));
}
